import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isAgentOwnerAccess } from 'pages/agents/utils/agent-share-access';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import type {
  Agent,
  AgentFormality,
  AgentResponseStyle,
  AgentTechnicalLevel,
  IUpdateAgentPersonaPayload,
} from '../agent-chat-module/types/agent.service.type';
import { useUpdateAgentPersona, useUpdateWidget } from './use-agents';

export type AgentPersonaValues = {
  name: string;
  description: string;
  agent_type: boolean;
  visibility: ResourceVisibility;
};

export const getAgentPersonaValues = (agent: Agent): AgentPersonaValues => ({
  name: agent.name || '',
  description: agent.description || '',
  agent_type: agent.is_any_agent || false,
  visibility: agent.visibility ?? DEFAULT_RESOURCE_VISIBILITY,
});

const buildUpdatePayload = (
  agent: Agent,
  values: AgentPersonaValues,
  projectKey: string,
  includeVisibility: boolean
): IUpdateAgentPersonaPayload => {
  const payload: IUpdateAgentPersonaPayload = {
    agent_id: agent.id,
    agent_type: agent.agent_type || '',
    name: values.name.trim(),
    language: agent.language || '',
    tags: agent.tags ?? [],
    domain_expertise: agent.domain_expertise ?? [],
    role: agent.role ?? '',
    description: values.description.trim(),
    logo_url: agent.logo_url || '',
    logo_id: agent.logo_id || '',
    response_style: agent.response_style as AgentResponseStyle,
    formality_level: agent.formality_level as AgentFormality,
    technical_level: agent.technical_level as AgentTechnicalLevel,
    project_key: projectKey,
    is_any_agent: values.agent_type,
  };

  if (includeVisibility) {
    payload.visibility = values.visibility;
  }

  return payload;
};

/** Saves an agent's name, description, type and visibility (visibility is owner-only). */
export function useSaveAgentPersona(projectKey: string) {
  const { t } = useTranslation();
  const userId = useCommonStore((state) => state.user?.id);
  const { mutateAsync: updateAgentPersona } = useUpdateAgentPersona();
  const { mutateAsync: updateWidget } = useUpdateWidget();

  return useCallback(
    async (agent: Agent, values: AgentPersonaValues) => {
      const isOwner = isAgentOwnerAccess(agent, userId);
      await updateAgentPersona(buildUpdatePayload(agent, values, projectKey, isOwner));

      // Widget / embed updates are owner-only (FE contract).
      if (!isOwner) return;

      const name = values.name.trim();
      await updateWidget({
        agent_id: agent.id,
        project_key: projectKey,
        regen_widget_id: false,
        widget: {
          ...agent.widget,
          name,
          greeting:
            agent.widget?.greeting ||
            (t('WELCOME_WITH_AGENT_NAME', { name }) ||
              `Welcome! My name is ${name}. If you need any assistance or have any questions, I'm here to help.`),
        },
      });
    },
    [projectKey, t, updateAgentPersona, updateWidget, userId]
  );
}
